import type {
  PlayerChatConversation,
  PlayerChatCapabilities,
  PlayerChatKind,
  PlayerChatMessage,
  PlayerGameActivity,
  PlayerMatchmakerQueue,
  PlayerParty,
  PlayerPartyMember,
  PlayerSocialEventEnvelope,
  PlayerSocialFriend,
  PlayerSocialState,
} from '@/api/public'
import { playerLastSeenPresentation } from './playerPublicStatusPresentation.ts'

export type PlayerDockSurface = 'none' | 'friends' | 'party' | 'chat'
export type PlayerSocialStatusKind = 'playing' | 'spectating' | 'site-online' | 'offline'
export type PlayerSocialStatusPresentation = {
  kind: PlayerSocialStatusKind
  label: string
  liveID: string | null
  title: string
}
export const playerChatRetentionMs = 7 * 24 * 60 * 60 * 1000
export const playerChatMessageGroupGapMs = 5 * 60 * 1000
export const playerIncomingPartyInviteLimit = 20
export const playerIncomingPartyInviteLimitMessage =
  'Достигнут лимит входящих приглашений. Отклоните ненужные приглашения.'

export function canSendPlayerChat(
  capabilities: PlayerChatCapabilities,
  kind: PlayerChatKind,
): boolean {
  const explicit = capabilities.send?.[kind]
  return typeof explicit === 'boolean'
    ? explicit
    : Boolean(capabilities[kind])
}

export type PlayerChatChangedHint = {
  conversationID: string
  kind: PlayerChatKind
  chatEpoch: string
  messageID: string
  lastMessageID: string
  messageType: 'user' | 'system'
  senderSteamID?: string
  messageCreatedAt?: string
}

const playerEventKinds = new Set<PlayerSocialEventEnvelope['event']>([
  'social.snapshot',
  'social.changed',
  'presence.changed',
  'activity.changed',
  'party.changed',
  'chat.changed',
  'communication.policy_changed',
])

const partyErrorLabels: Record<string, string> = {
  party_full: 'В группе уже пять игроков.',
  party_leader_required: 'Это действие доступно только лидеру группы.',
  party_leader_must_transfer: 'Перед выходом передайте лидерство или распустите группу.',
  actor_already_in_party: 'Вы уже состоите в группе с другими игроками.',
  target_already_in_party: 'Игрок уже состоит в группе с другими игроками.',
  already_party_member: 'Игрок уже находится в вашей группе.',
  // Rolling-deploy fallback for backends predating the actor/target split.
  already_in_party: 'Игрок уже состоит в группе.',
  party_ready_check: 'Состав нельзя менять во время подтверждения игры.',
  party_in_match: 'Состав нельзя менять, пока игровое лобби не создано.',
  party_activity_changed: 'Состояние поиска изменилось. Попробуйте ещё раз.',
  party_member_not_found: 'Этот игрок уже покинул группу. Состав обновлён.',
  party_member_ineligible: 'Один из участников сейчас не может войти в эту группу.',
  party_target_notifications_ignored: 'Игрок отключил приглашения от вас.',
  party_target_blocked: 'Между игроком и одним из участников группы действует блокировка.',
  party_target_invite_limit: 'У игрока достигнут лимит входящих приглашений.',
  party_target_unavailable: 'Игрок сейчас недоступен для приглашения.',
  party_revision_conflict: 'Состав группы обновился. Повторите действие.',
  party_precondition_required: 'Состав группы обновился. Повторите действие.',
  invalid_party_precondition: 'Не удалось проверить версию состава. Данные группы обновлены.',
  party_invite_not_found: 'Приглашение уже недоступно.',
  party_invite_cooldown: 'Подождите 10 секунд перед повторным приглашением этого игрока.',
  social_target_unavailable: 'Игрок сейчас недоступен.',
  social_limit_reached: 'Достигнут лимит для этого социального действия.',
  already_friends: 'Этот игрок уже в списке друзей.',
  incoming_friend_request_exists: 'У вас уже есть входящая заявка от этого игрока.',
  friend_request_not_found: 'Заявка уже недоступна.',
  invalid_steam_id: 'Введите корректный SteamID64.',
  invalid_request: 'Проверьте данные и повторите действие.',
  chat_epoch_changed: 'Чат был перезапущен. Загружена актуальная история.',
  chat_channel_disabled: 'Этот канал отключён на сервере.',
  chat_unavailable: 'Чат временно недоступен.',
  chat_target_unavailable: 'Этот чат сейчас недоступен.',
  chat_conversation_not_found: 'Этот чат уже недоступен.',
  chat_message_not_found: 'Сообщение уже исчезло из временной истории.',
  chat_retry: 'Состояние чата изменилось. Попробуйте ещё раз.',
  invalid_message_text: 'Сообщение пустое или слишком длинное.',
  chat_limit_reached: 'Слишком много сообщений. Подождите немного.',
  chat_send_restricted: 'Отправка сообщений ограничена. История и входящие сообщения доступны.',
  idempotency_conflict: 'Статус прошлой отправки неясен. Обновите текст перед повторной отправкой.',
}

export function togglePlayerDockSurface(
  current: PlayerDockSurface,
  requested: Exclude<PlayerDockSurface, 'none'>,
): PlayerDockSurface {
  return current === requested ? 'none' : requested
}

export function playerDisplayName(
  player: { steam_id: string; persona_name?: string | null },
): string {
  return player.persona_name?.trim() || player.steam_id
}

export type PlayerPartyInviteUnavailableReason =
  | 'action_pending'
  | 'roster_locked'
  | 'actor_not_member'
  | 'target_already_member'
  | 'party_full'
  | 'cooldown'

export type PlayerPartyInviteAvailability = {
  allowed: boolean
  reason: PlayerPartyInviteUnavailableReason | null
  retryAfterSeconds: number
}

export type PlayerPartyInviteAvailabilityOptions = {
  targetSteamID?: string
  rosterLocked?: boolean
  actionPending?: boolean
  cooldownSeconds?: number
}

export function playerPartyInviteAvailability(
  party: Pick<PlayerParty, 'members'> | null,
  actorSteamID: string,
  options: PlayerPartyInviteAvailabilityOptions = {},
): PlayerPartyInviteAvailability {
  const actor = actorSteamID.trim()
  const target = options.targetSteamID?.trim() ?? ''
  const retryAfterSeconds = Number.isFinite(options.cooldownSeconds)
    ? Math.max(0, Math.ceil(options.cooldownSeconds ?? 0))
    : 0

  if (options.actionPending) {
    return { allowed: false, reason: 'action_pending', retryAfterSeconds }
  }
  if (options.rosterLocked) {
    return { allowed: false, reason: 'roster_locked', retryAfterSeconds }
  }
  if (!actor || (party && !party.members.some(member => member.steam_id === actor))) {
    return { allowed: false, reason: 'actor_not_member', retryAfterSeconds }
  }
  if (target && party?.members.some(member => member.steam_id === target)) {
    return { allowed: false, reason: 'target_already_member', retryAfterSeconds }
  }
  if ((party?.members.length ?? 1) >= 5) {
    return { allowed: false, reason: 'party_full', retryAfterSeconds }
  }
  if (retryAfterSeconds > 0) {
    return { allowed: false, reason: 'cooldown', retryAfterSeconds }
  }
  return { allowed: true, reason: null, retryAfterSeconds: 0 }
}

export function playerPartyInviteUnavailableReason(
  availability: PlayerPartyInviteAvailability,
  rosterLockReason = '',
): string {
  switch (availability.reason) {
    case 'action_pending':
      return 'Дождитесь завершения действия с группой.'
    case 'roster_locked':
      return rosterLockReason || 'Состав группы временно заблокирован.'
    case 'actor_not_member':
      return 'Состав группы изменился. Дождитесь синхронизации.'
    case 'target_already_member':
      return 'Игрок уже в группе.'
    case 'party_full':
      return 'Группа заполнена.'
    case 'cooldown':
      return `Можно снова пригласить через ${availability.retryAfterSeconds} сек.`
    default:
      return ''
  }
}

export function canStartPartyMatchmaking(
  party: Pick<PlayerParty, 'members'> | null,
  actorSteamID: string,
): boolean {
  if (!party) return true
  return Boolean(actorSteamID) && party.members.some(member => member.steam_id === actorSteamID)
}

export function canCancelPartyMatchmaking(
  party: Pick<PlayerParty, 'members'> | null,
  actorSteamID: string,
): boolean {
  if (!party) return true
  return Boolean(actorSteamID) && party.members.some(member => member.steam_id === actorSteamID)
}

export function sortSocialFriends(friends: readonly PlayerSocialFriend[]): PlayerSocialFriend[] {
  return [...friends].sort((left, right) => {
    const nameOrder = playerDisplayName(left.player).localeCompare(
      playerDisplayName(right.player),
      'ru',
      { sensitivity: 'base' },
    )
    if (nameOrder !== 0) return nameOrder
    return left.player.steam_id.localeCompare(right.player.steam_id)
  })
}

export function playerSocialStatusPresentation(
  presence: {
    online: boolean
    last_seen_at?: string | null
  } | null | undefined,
  gameActivity: PlayerGameActivity | null | undefined,
  now: Date = new Date(),
): PlayerSocialStatusPresentation {
  const liveID = gameActivity?.live_id?.trim() || null
  if (gameActivity?.kind === 'playing') {
    return {
      kind: 'playing',
      label: 'В игре',
      liveID,
      title: liveID ? 'В игре: открыть матч' : 'В игре',
    }
  }
  if (gameActivity?.kind === 'spectating') {
    return {
      kind: 'spectating',
      label: 'Смотрит игру',
      liveID,
      title: liveID ? 'Смотрит игру: открыть матч' : 'Смотрит игру',
    }
  }
  if (presence?.online) {
    return {
      kind: 'site-online',
      label: 'На сайте',
      liveID: null,
      title: 'Сейчас на сайте',
    }
  }
  const lastSeen = playerLastSeenPresentation(presence?.last_seen_at, now)
  if (lastSeen) {
    return {
      kind: 'offline',
      label: lastSeen.label,
      liveID: null,
      title: lastSeen.title,
    }
  }
  return {
    kind: 'offline',
    label: 'Не был на сайте',
    liveID: null,
    title: 'Нет данных о посещении сайта',
  }
}

export function playerSocialStatusNeedsClock(
  presence: {
    online: boolean
    last_seen_at?: string | null
  } | null | undefined,
  gameActivity: PlayerGameActivity | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (gameActivity || presence?.online || !Number.isFinite(nowMs)) return false
  const rawLastSeenAt = presence?.last_seen_at
  const lastSeenAt = rawLastSeenAt ? Date.parse(rawLastSeenAt) : Number.NaN
  if (!Number.isFinite(lastSeenAt)) return false
  return Math.max(0, nowMs - lastSeenAt) < 7 * 24 * 60 * 60_000
}

export function isValidFriendSteamID(raw: string, currentSteamID: string): boolean {
  const steamID = raw.trim()
  if (!/^[1-9][0-9]*$/.test(steamID) || steamID === currentSteamID) return false
  const maxSignedID = '9223372036854775807'
  return (
    steamID.length < maxSignedID.length ||
    (steamID.length === maxSignedID.length && steamID <= maxSignedID)
  )
}

export function collapsedPlayerChatUnreadCount(
  conversations: readonly PlayerChatConversation[],
): number {
  const unreadByTarget = new Map<string, {
    count: number
    updatedAt: number
  }>()
  for (const conversation of conversations) {
    let targetKey = ''
    if (conversation.kind === 'party') {
      targetKey = chatConversationTargetKey(
        'party',
        conversation.party_id ?? '',
      )
    } else if (conversation.kind === 'dm') {
      const peerSteamID = conversation.peer?.steam_id.trim() ?? ''
      if (!peerSteamID) continue
      targetKey = chatConversationTargetKey('dm', '', peerSteamID)
    } else {
      continue
    }
    const rawCount = conversation.unread_count
    const count = (
      Number.isFinite(rawCount) && rawCount > 0
        ? Math.min(Number.MAX_SAFE_INTEGER, Math.floor(rawCount))
        : 0
    )
    const parsedUpdatedAt = Date.parse(conversation.updated_at)
    const updatedAt = Number.isFinite(parsedUpdatedAt)
      ? parsedUpdatedAt
      : Number.NEGATIVE_INFINITY
    const current = unreadByTarget.get(targetKey)
    if (
      !current ||
      updatedAt > current.updatedAt ||
      (updatedAt === current.updatedAt && count > current.count)
    ) {
      unreadByTarget.set(targetKey, { count, updatedAt })
    }
  }

  let total = 0
  for (const { count } of unreadByTarget.values()) {
    total = Math.min(Number.MAX_SAFE_INTEGER, total + count)
  }
  return total
}

export function hasGlobalPlayerChatUnread(
  conversations: readonly PlayerChatConversation[],
): boolean {
  let latest: PlayerChatConversation | null = null
  for (const conversation of conversations) {
    if (conversation.kind !== 'global') continue
    if (!latest || isNewerChatConversationSummary(conversation, latest)) {
      latest = conversation
    }
  }
  return Boolean(
    latest &&
    Number.isFinite(latest.unread_count) &&
    latest.unread_count > 0,
  )
}

function isNewerChatConversationSummary(
  candidate: PlayerChatConversation,
  current: PlayerChatConversation,
): boolean {
  const candidateUpdatedAt = Date.parse(candidate.updated_at)
  const currentUpdatedAt = Date.parse(current.updated_at)
  const normalizedCandidateUpdatedAt = Number.isFinite(candidateUpdatedAt)
    ? candidateUpdatedAt
    : Number.NEGATIVE_INFINITY
  const normalizedCurrentUpdatedAt = Number.isFinite(currentUpdatedAt)
    ? currentUpdatedAt
    : Number.NEGATIVE_INFINITY
  if (normalizedCandidateUpdatedAt !== normalizedCurrentUpdatedAt) {
    return normalizedCandidateUpdatedAt > normalizedCurrentUpdatedAt
  }
  if (candidate.chat_epoch === current.chat_epoch) {
    const messageOrder = compareDecimalIDs(
      candidate.last_message_id,
      current.last_message_id,
    )
    if (messageOrder !== 0) return messageOrder > 0
    const readOrder = compareDecimalIDs(
      candidate.last_read_message_id,
      current.last_read_message_id,
    )
    if (readOrder !== 0) return readOrder > 0
  }
  return candidate.id > current.id
}

export function recentDMConversations(
  conversations: readonly PlayerChatConversation[],
  maxConversations = 15,
): PlayerChatConversation[] {
  return conversations
    .filter(conversation => (
      conversation.kind === 'dm' &&
      Boolean(conversation.peer?.steam_id) &&
      !conversation.recent_hidden &&
      conversation.last_message_id !== '0' &&
      Number.isFinite(Date.parse(conversation.updated_at))
    ))
    .sort((left, right) => {
      const activityDelta = Date.parse(right.updated_at) -
        Date.parse(left.updated_at)
      if (activityDelta !== 0) return activityDelta
      return right.id.localeCompare(left.id)
    })
    .slice(0, Math.max(0, Math.floor(maxConversations)))
}

export function formatPlayerChatMessageTime(
  raw: string,
  now: Date = new Date(),
): string {
  const value = Date.parse(raw)
  const nowValue = now.getTime()
  if (!Number.isFinite(value) || !Number.isFinite(nowValue)) return ''
  const message = new Date(value)
  const calendarDay = (date: Date) => Date.UTC(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  ) / 86_400_000
  const dayDelta = calendarDay(now) - calendarDay(message)
  const time = `${padTwo(message.getHours())}:${padTwo(message.getMinutes())}`
  if (dayDelta === 0) return time
  if (dayDelta === 1) return `вчера ${time}`
  return `${padTwo(message.getDate())}.${padTwo(message.getMonth() + 1)} ${time}`
}

export function formatPlayerChatMessageClockTime(raw: string): string {
  const value = Date.parse(raw)
  if (!Number.isFinite(value)) return ''
  const message = new Date(value)
  return `${padTwo(message.getHours())}:${padTwo(message.getMinutes())}`
}

export function formatPlayerChatMessageAccessibleTime(raw: string): string {
  const value = Date.parse(raw)
  if (!Number.isFinite(value)) return ''
  const message = new Date(value)
  return [
    `${padTwo(message.getDate())}.${padTwo(message.getMonth() + 1)}.${message.getFullYear()}`,
    `${padTwo(message.getHours())}:${padTwo(message.getMinutes())}:${padTwo(message.getSeconds())}`,
  ].join(' ')
}

export function playerChatLocalDayKey(raw: string): string {
  const value = Date.parse(raw)
  if (!Number.isFinite(value)) return ''
  return localCalendarDayKey(new Date(value))
}

export function formatPlayerChatDayLabel(raw: string): string {
  const value = Date.parse(raw)
  if (!Number.isFinite(value)) return ''
  return playerChatDayLabelFormatter.format(new Date(value))
}

export function continuesPlayerChatMessageGroup(
  previous: PlayerChatMessage,
  current: PlayerChatMessage,
  maxGapMs = playerChatMessageGroupGapMs,
): boolean {
  if (
    isPlayerChatSystemMessage(previous) ||
    isPlayerChatSystemMessage(current) ||
    previous.sender.steam_id !== current.sender.steam_id ||
    previous.conversation_id !== current.conversation_id ||
    previous.chat_epoch !== current.chat_epoch
  ) {
    return false
  }
  const previousTime = Date.parse(previous.created_at)
  const currentTime = Date.parse(current.created_at)
  if (
    !Number.isFinite(previousTime) ||
    !Number.isFinite(currentTime) ||
    !Number.isFinite(maxGapMs)
  ) {
    return false
  }
  const previousDate = new Date(previousTime)
  const currentDate = new Date(currentTime)
  if (localCalendarDayKey(previousDate) !== localCalendarDayKey(currentDate)) {
    return false
  }
  const gap = currentTime - previousTime
  return gap >= 0 && gap <= Math.max(0, maxGapMs)
}

export function isPlayerChatSystemMessage(message: PlayerChatMessage): boolean {
  return message.message_type === 'system'
}

export function formatPlayerChatSystemMessage(message: PlayerChatMessage): string {
  if (!isPlayerChatSystemMessage(message)) return message.text
  const actor = playerDisplayName(message.sender)
  switch (message.system_event?.type) {
    case 'member_joined': {
      const invitedBy = message.system_event.invited_by
      return invitedBy
        ? `${actor} присоединяется к группе по приглашению ${playerDisplayName(invitedBy)}`
        : `${actor} присоединяется к группе`
    }
    case 'matchmaking_started':
      return `${actor} запускает поиск матча`
    case 'matchmaking_cancelled':
      return `${actor} отменяет поиск матча`
    default:
      return message.text
  }
}

export function chatConversationTargetKey(
  kind: PlayerChatKind,
  partyID = '',
  peerSteamID = '',
): string {
  switch (kind) {
    case 'party':
      return `party:${partyID || 'solo'}`
    case 'dm':
      return peerSteamID ? `dm:${peerSteamID}` : ''
    case 'global':
      return 'global'
  }
}

function padTwo(value: number): string {
  return String(value).padStart(2, '0')
}

const playerChatDayLabelFormatter = new Intl.DateTimeFormat('ru-RU', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

function localCalendarDayKey(date: Date): string {
  return [
    String(date.getFullYear()).padStart(4, '0'),
    padTwo(date.getMonth() + 1),
    padTwo(date.getDate()),
  ].join('-')
}

export function reconcileChatMessages(
  current: readonly PlayerChatMessage[],
  incoming: readonly PlayerChatMessage[],
  chatEpoch: string,
  maxMessages = 200,
  conversationID = '',
): PlayerChatMessage[] {
  const byID = new Map<string, PlayerChatMessage>()
  for (const message of current) {
    if (
      message.chat_epoch === chatEpoch &&
      (!conversationID || message.conversation_id === conversationID)
    ) {
      byID.set(message.id, message)
    }
  }
  for (const message of incoming) {
    if (
      message.chat_epoch === chatEpoch &&
      (!conversationID || message.conversation_id === conversationID)
    ) {
      byID.set(message.id, message)
    }
  }
  return [...byID.values()]
    .sort((left, right) => compareDecimalIDs(left.id, right.id))
    .slice(-Math.max(1, maxMessages))
}

export function latestChatMessageKey(messages: readonly PlayerChatMessage[]): string {
  const message = messages.at(-1)
  return message ? `${message.chat_epoch}:${message.id}` : ''
}

export function chatConversationHighWaterKey(
  conversation: PlayerChatConversation | null,
): string {
  return conversation
    ? `${conversation.id}:${conversation.chat_epoch}:${conversation.last_message_id}`
    : ''
}

export function pruneExpiredChatMessages(
  messages: readonly PlayerChatMessage[],
  nowMs = Date.now(),
  retentionMs = playerChatRetentionMs,
): PlayerChatMessage[] {
  const cutoff = nowMs - Math.max(0, retentionMs)
  return messages.filter(message => {
    const createdAt = Date.parse(message.created_at)
    return Number.isFinite(createdAt) && createdAt > cutoff
  })
}

export function queueAllowsPartySize(
  queue: PlayerMatchmakerQueue,
  partySize: number,
): boolean {
  const normalizedSize = Math.max(1, Math.floor(partySize))
  if (normalizedSize === 1) return true
  const policy = queue.party_size_limits.find(limit => limit.size === normalizedSize)
  return Boolean(policy && policy.max_waiting_parties > 0)
}

export function partyMembershipKey(
  party: { id: string; members: PlayerPartyMember[] } | null,
  selfSteamID: string,
): string {
  if (!party || !selfSteamID) return ''
  const self = party.members.find(member => member.steam_id === selfSteamID)
  return self ? `${party.id}:${self.joined_at}` : ''
}

export function focusTrapDestination(
  activeIndex: number,
  focusableCount: number,
  backwards: boolean,
): number | null {
  if (focusableCount <= 0) return null
  if (activeIndex < 0) return backwards ? focusableCount - 1 : 0
  if (backwards && activeIndex === 0) return focusableCount - 1
  if (!backwards && activeIndex === focusableCount - 1) return 0
  return null
}

export function parsePlayerSocialEvent(raw: string): PlayerSocialEventEnvelope | null {
  try {
    const value = JSON.parse(raw) as Partial<PlayerSocialEventEnvelope>
    if (!value || typeof value !== 'object') return null
    if (typeof value.event !== 'string' || !playerEventKinds.has(value.event as PlayerSocialEventEnvelope['event'])) {
      return null
    }
    if (typeof value.ts !== 'string' || !value.data || typeof value.data !== 'object') return null
    return value as PlayerSocialEventEnvelope
  } catch {
    return null
  }
}

export function isPlayerCommunicationPolicyChanged(
  event: PlayerSocialEventEnvelope,
): boolean {
  if (event.event !== 'communication.policy_changed') return false
  const data = event.data as Record<string, unknown>
  return data.refresh === true
}

export function parsePlayerChatChangedHint(
  event: PlayerSocialEventEnvelope,
): PlayerChatChangedHint | null {
  if (event.event !== 'chat.changed') return null
  const data = event.data as Record<string, unknown>
  if (
    data.refresh !== true ||
    typeof data.conversation_id !== 'string' ||
    !data.conversation_id ||
    (data.kind !== 'global' && data.kind !== 'party' && data.kind !== 'dm') ||
    typeof data.chat_epoch !== 'string' ||
    !data.chat_epoch ||
    typeof data.message_id !== 'string' ||
    !/^[1-9][0-9]*$/.test(data.message_id) ||
    typeof data.last_message_id !== 'string' ||
    !/^[1-9][0-9]*$/.test(data.last_message_id)
  ) {
    return null
  }
  const messageType = data.message_type === undefined
    ? 'user'
    : data.message_type
  if (messageType !== 'user' && messageType !== 'system') return null
  if (
    messageType === 'user' &&
    (
      typeof data.sender_steam_id !== 'string' ||
      !/^[1-9][0-9]*$/.test(data.sender_steam_id) ||
      typeof data.message_created_at !== 'string' ||
      !Number.isFinite(Date.parse(data.message_created_at))
    )
  ) {
    return null
  }
  const hint: PlayerChatChangedHint = {
    conversationID: data.conversation_id,
    kind: data.kind,
    chatEpoch: data.chat_epoch,
    messageID: data.message_id,
    lastMessageID: data.last_message_id,
    messageType,
  }
  if (messageType === 'user') {
    hint.senderSteamID = data.sender_steam_id as string
    hint.messageCreatedAt = data.message_created_at as string
  }
  return hint
}

export function applyPresenceChanged(
  state: PlayerSocialState,
  event: PlayerSocialEventEnvelope,
  selfSteamID: string,
): PlayerSocialState | null {
  if (event.event !== 'presence.changed') return state
  const data = event.data as {
    steam_id?: unknown
    online?: unknown
    version?: unknown
    epoch?: unknown
    last_seen_at?: unknown
  }
  if (
    typeof data.steam_id !== 'string' ||
    typeof data.online !== 'boolean' ||
    typeof data.version !== 'string' ||
    typeof data.epoch !== 'string'
  ) {
    return null
  }
  const steamID = data.steam_id
  const online = data.online
  const version = data.version
  const epoch = data.epoch
  if (epoch !== state.presence_epoch || !/^\d+$/.test(version)) return null
  const hasLastSeenAt = Object.prototype.hasOwnProperty.call(data, 'last_seen_at')
  const lastSeenAt = data.last_seen_at
  if (
    hasLastSeenAt &&
    lastSeenAt !== null &&
    (
      typeof lastSeenAt !== 'string' ||
      !Number.isFinite(Date.parse(lastSeenAt))
    )
  ) {
    return null
  }
  if (steamID === selfSteamID) {
    if (!/^\d+$/.test(state.self_presence.version)) return null
    if (compareDecimalIDs(version, state.self_presence.version) <= 0) return state
    return {
      ...state,
      self_presence: {
        ...state.self_presence,
        online,
        version,
        ...presenceLastSeenPatch(
          state.self_presence.last_seen_at,
          hasLastSeenAt,
          lastSeenAt,
        ),
      },
    }
  }
  const currentFriend = state.friends.find(friend => friend.player.steam_id === steamID)
  if (!currentFriend) return state
  if (!/^\d+$/.test(currentFriend.presence.version)) return null
  if (compareDecimalIDs(version, currentFriend.presence.version) <= 0) return state
  let changed = false
  const friends = state.friends.map(friend => {
    if (friend.player.steam_id !== steamID) return friend
    changed = true
    return {
      ...friend,
      presence: {
        ...friend.presence,
        online,
        version,
        ...presenceLastSeenPatch(
          friend.presence.last_seen_at,
          hasLastSeenAt,
          lastSeenAt,
        ),
      },
    }
  })
  return changed ? { ...state, friends } : state
}

function presenceLastSeenPatch(
  current: string | null | undefined,
  hasIncoming: boolean,
  incoming: unknown,
): { last_seen_at?: string | null } {
  if (!hasIncoming) return {}

  const currentTime = typeof current === 'string'
    ? Date.parse(current)
    : Number.NaN
  if (typeof incoming === 'string') {
    const incomingTime = Date.parse(incoming)
    if (Number.isFinite(currentTime) && currentTime >= incomingTime) {
      return { last_seen_at: current }
    }
    return { last_seen_at: incoming }
  }
  if (Number.isFinite(currentTime)) {
    return { last_seen_at: current }
  }
  return { last_seen_at: null }
}

export function playerDockErrorMessage(error: unknown): string {
  if (isRequestError(error)) {
    if (error.code === 'chat_send_restricted') {
      const expiresAt = (error as Error & { expiresAt?: unknown }).expiresAt
      if (typeof expiresAt === 'string') {
        const date = new Date(expiresAt)
        if (!Number.isNaN(date.getTime())) {
          return `Отправка сообщений ограничена до ${date.toLocaleString('ru-RU')}. История и входящие сообщения доступны.`
        }
      }
    }
    if (error.code && partyErrorLabels[error.code]) return partyErrorLabels[error.code]
    if (error.status === 429) return 'Слишком много действий. Попробуйте немного позже.'
    if (error.status === 503) return 'Социальный сервис временно недоступен.'
  }
  return error instanceof Error && error.message
    ? error.message
    : 'Не удалось выполнить действие.'
}

export function playerPartyInviteAcceptanceErrorMessage(error: unknown): string {
  if (isRequestError(error)) {
    switch (error.code) {
      case 'actor_already_in_party':
        return 'Сначала покиньте текущую группу с другими игроками, затем примите приглашение.'
      case 'already_in_party':
        return 'Вы уже состоите в группе. Обновите состояние и повторите действие.'
      case 'party_target_blocked':
        return 'Нельзя присоединиться: между вами и участником группы действует блокировка.'
      case 'party_target_notifications_ignored':
        return 'Нельзя присоединиться: приглашения от этой группы отключены.'
      case 'party_target_invite_limit':
        return 'Нельзя присоединиться: достигнут лимит входящих приглашений.'
      case 'party_target_unavailable':
        return 'Сейчас нельзя присоединиться к этой группе.'
    }
  }
  return playerDockErrorMessage(error)
}

export function playerIncomingPartyInviteLimitNotice(count: number): string {
  return count >= playerIncomingPartyInviteLimit
    ? playerIncomingPartyInviteLimitMessage
    : ''
}

export function playerPartyInviteBulkDeclineConfirmation(): string {
  return 'Отклонить все входящие приглашения в группу? Это действие нельзя отменить.'
}

export function createPlayerActionID(
  prefix: 'page' | 'chat',
  randomUUID: () => string = defaultRandomUUID,
): string {
  const token = randomUUID().replace(/[^A-Za-z0-9_-]/g, '')
  return `${prefix}_${token}`.slice(0, 64).padEnd(16, '0')
}

export function compareDecimalIDs(left: string, right: string): number {
  const normalizedLeft = left.replace(/^0+/, '') || '0'
  const normalizedRight = right.replace(/^0+/, '') || '0'
  if (normalizedLeft.length !== normalizedRight.length) {
    return normalizedLeft.length - normalizedRight.length
  }
  return normalizedLeft.localeCompare(normalizedRight)
}

function defaultRandomUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now().toString(36)}_${Math.random().toString(36).slice(2)}_${Math.random().toString(36).slice(2)}`
}

function isRequestError(
  error: unknown,
): error is Error & { status: number; code: string | null } {
  return (
    error instanceof Error &&
    typeof (error as { status?: unknown }).status === 'number' &&
    (
      (error as { code?: unknown }).code === null ||
      typeof (error as { code?: unknown }).code === 'string'
    )
  )
}
